import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  CheckCircle,
  CheckmarkIcon,
  EmptyCircle,
  LockIcon,
  PencilIcon,
  Plus,
  RightArrow,
  UploadIcon,
  DownloadIcon
} from '../../../icons/SvgIcons';
import {
  TextButton,
  PrimaryButton,
  WhiteButton
} from '../../ui/Button/Buttons';
import { Tooltip } from '../../ui/Tooltip/Tooltip';
import { Pill } from '../../ui/Pill/Pill';
import { PillVariant } from '../../ui/Pill/Pill.types';
import { SideDrawer } from '../../ui/SideDrawer/SideDrawer';
import Modal from '../../ui/Modal/Modal';
import { FileUpload } from '../../ui/FileUpload/FileUpload';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import {
  fetchRequirementStepsByAISystemId,
  fetchStepActionsByStepId,
  setFromRequirement,
  setRequirementActionCompleted
} from '../../../store/slices/requirementStepsSlice';
import CheckboxItem from '../../ui/Checkbox/Checkbox';
import { Input } from '../../ui/Input/Input';
import { updateLl144FirstUseDate } from '../../../services/Ll144AssessmentService';
import { acknowledgeLeadTime } from '../../../services/RequirementStepsService';
import { AttentionMessage } from '../../ui/AttentionMessage/AttentionMessage';
import {
  LeadTimeVerdict,
  StepAction,
  StepStatus
} from '../../../types/RequirementSteps.types';
import { useNavigate } from 'react-router-dom';
import {
  supportedFiles,
  uploadFileLimits,
  radioButtonCardOptions,
  friaOptionsCardOptions,
  dpiaOptionsCardOptions
} from '../Documentation/Documentation.const';
import { useDocumentUpload } from '../../../hooks/useDocumentUpload';
import { resetFileState } from '../../../store/slices/fileUploadSlice';
import { Toast } from '../../ui/Toast/Toast';
import { RadioButtonCard } from '../../ui/RadioButton/RadioButtonCard';
import {
  setAnnexIVFieldsState,
  setCreateAnnexIVDraftResponse,
  setCreateDPIAResponse,
  setCreateEUDeclarationResponse,
  setCreateFRIAResponse,
  setDPIAField,
  setDPIAFieldsState,
  setEUDeclarationFieldsState,
  setEUDeclarationStatus,
  setFRIAFieldsState
} from '../../../store/slices/documentationSlice';
import {
  handleOpenSidePanel,
  setSidePanelContent,
  setSidePanelTitle
} from '../../../store/slices/sidePanelSlice';
import { createAnnexIVDraft } from '../../../services/AnnexIVService';
import {
  createEUDeclarationDraft,
  getEUDeclarationByAiSystem
} from '../../../services/EUDeclarationService';
import {
  mapAnnexContentToFields,
  mapDPIAContentToFields,
  mapEUDeclarationContentToFields,
  mapFRIAContentToFields
} from '../../../utils/documentationHelpers';
import {
  createFRIADraft,
  getFRIAByAiSystem
} from '../../../services/FRIAService';
import {
  createDPIADraft,
  getDPIAByAiSystem
} from '../../../services/DPIAService';
import {
  getDocumentation,
  signDocument,
  downloadDocument
} from '../../../services/DocumentationService';
import { finishCompliance } from '../../../services/RequirementStepsService';
import {
  createComplianceRecord,
  getComplianceRecord,
  completeComplianceRecord
} from '../../../services/ComplianceRecordService';
import { ComplianceRecord } from '../../../types/ComplianceRecord.types';
import { ComplianceRecordDrawerForm } from './ComplianceRecordDrawerForm';
import { Steps } from '../../steps/Steps';
import { useAuthorization } from '../../../hooks/useAuthorization';
import { RequirementEvidenceControl } from '../RequirementEvidence/RequirementEvidenceControl';
import { evidenceUploadLabel } from '../RequirementEvidence/RequirementEvidenceDocument';
import {
  RequirementEvidenceDocumentModal,
  EvidenceUploadTarget
} from '../RequirementEvidence/RequirementEvidenceDocumentModal';
import { showToast as showGlobalToast } from '../../../store/slices/toastSlice';
import { formatDateOnly } from '../../../utils/Date.util';
import {
  getBiasAuditExpiryState,
  getNoticeLeadTimeState,
  BiasAuditExpiryKind,
  NoticeLeadTimeKind
} from '../../../utils/RequirementSteps.util';

type RequirementListStepProps = {
  stepId: string;
  stepKey: string;
  order: number;
  title: string;
  status: StepStatus;
  overview: string;
  locked: boolean;
  // The list's ordering verdict. A card in an orderless pack must not present
  // itself as step N of a flow. Absent means "not known to be orderless" - the
  // sequential presentation, i.e. the EU behaviour.
  orderless?: boolean;
  expiresAt?: string;
  renewableAt?: string;
  // The server's verdict on this step's notice window, for a pack obligation that declares one.
  // Absent on every other step. A GATE: the server holds a breached or unverifiable step out of
  // COMPLETED, so this explains a status the card would otherwise present without a reason.
  leadTime?: LeadTimeVerdict | null;
  // When the breach was accepted, if it was. An acknowledged step reads COMPLETED while `leadTime`
  // still reads NOT_MET — both are shown, so an accepted breach never renders as a clean step.
  leadTimeAcknowledgedAt?: string | null;
  onContinue?: () => void;
};

// Steps whose actions are attestation checkboxes the user ticks directly
// (importer/distributor verification), rather than the provider/deployer model
// of one upload/generate button per step.
const CHECKBOX_STEP_KEYS = new Set<string>([
  'IMPORTER_CONFIRM_COMPLIANCE',
  'IMPORTER_AFFIX_IDENTITY',
  'DISTRIBUTOR_VERIFY_MARKINGS',
  'DISTRIBUTOR_VERIFY_UPSTREAM'
]);

// Steps whose actions are per-document uploads (importer Art. 23(5) retention).
// Each action uploads to its own document category; the backend then completes
// the matching action automatically (see document.service checkDocumentProgress).
const UPLOAD_STEP_KEYS = new Set<string>(['IMPORTER_PROVIDER_DOCUMENTATION']);

const IMPORTER_UPLOAD_CATEGORY_BY_ACTION_KEY: Record<string, string> = {
  IMPORTER_UPLOAD_DECLARATION_OF_CONFORMITY: 'EU_DECLARATION_OF_CONFORMITY',
  IMPORTER_UPLOAD_INSTRUCTIONS: 'INSTRUCTIONS',
  IMPORTER_UPLOAD_CERTIFICATE: 'CONFORMITY_ASSESSMENT'
};

// Final importer/distributor step: generate a consolidated compliance record
// from the prior verifications (+ importer identity/retention fields) and sign
// it. The record's own action is completed server-side once signed.
const RECORD_STEP_KEYS = new Set<string>([
  'IMPORTER_SIGN_COMPLIANCE_RECORD',
  'DISTRIBUTOR_SIGN_COMPLIANCE_RECORD'
]);

// `locked` changes the call to action only. A locked step's button is disabled
// (`isStepActionDisabled`), so it must not promise something the click cannot
// deliver - and the click opens THIS step's drawer, never a prerequisite.
// `label` stays status-only on purpose: the status pill and the drawer's
// `showActionButton` gate both read it.
const getStatusConfig = (status: StepStatus, locked = false) => {
  switch (status) {
    case 'COMPLETED':
      return {
        label: 'Completed',
        pillVariant: 'completed' as PillVariant,
        showButton: true,
        buttonText: 'View Details',
        numberClass: 'bg-purple-600 text-white'
      };
    case 'IN_PROGRESS':
      return {
        label: 'In Progress',
        pillVariant: 'in-progress' as PillVariant,
        showButton: true,
        buttonText: 'Continue Work',
        numberClass: 'border border-purple-600 bg-white text-purple-600'
      };
    case 'EXPIRED':
      return {
        label: 'Expired',
        pillVariant: 'error' as PillVariant,
        showButton: true,
        buttonText: 'Renew',
        numberClass: 'border border-rose-500 bg-white text-rose-600'
      };
    case 'NOT_STARTED':
    default:
      return {
        label: 'Not Started',
        pillVariant: 'not-started' as PillVariant,
        showButton: true,
        buttonText: locked ? 'Locked' : 'Start',
        numberClass: 'border border-gray-200 text-gray-550'
      };
  }
};

const getBorderClasses = (
  status: StepStatus,
  locked: boolean,
  expiryKind: BiasAuditExpiryKind = 'none',
  leadTimeKind: NoticeLeadTimeKind = 'none'
) => {
  if (expiryKind === 'expired') {
    return 'border-rose-400 hover:border-rose-600';
  }
  if (expiryKind === 'expiring-soon') {
    return 'border-amber-400 hover:border-amber-600';
  }
  // Same amber as "expiring soon", and deliberately so: both say the obligation needs attention
  // without being outright broken. Ranked below expiry — see the pill derivation.
  //
  // 'acknowledged' is excluded: that step IS closed, and the breach on it is recorded in the pill
  // and the info line. Painting it amber would keep raising an alarm the user has already answered.
  if (leadTimeKind !== 'none' && leadTimeKind !== 'acknowledged') {
    return 'border-amber-400 hover:border-amber-600';
  }
  switch (status) {
    case 'COMPLETED':
      return 'border-gray-100 hover:border-purple-800';
    case 'IN_PROGRESS':
      return 'border-purple-500 hover:border-purple-800';
    case 'EXPIRED':
      return 'border-rose-400 hover:border-rose-600';
    case 'NOT_STARTED':
    default:
      return locked
        ? 'border-gray-100 bg-gray-25 hover:border-gray-200'
        : 'border-gray-100 hover:border-gray-300 bg-gray-25';
  }
};

// Step keys with bespoke EU generator flows (see getActionButton). These must
// keep their custom UI even though the backend now persists an `evidenceKind`
// on their actions — otherwise the generic evidence controls would hijack them.
const EU_SPECIAL_STEP_KEYS = new Set([
  'CONNECT_REPOSITORY',
  'SUPPORTING_DOCUMENTATION',
  'ANNEX_IV',
  'CONFORMITY_ASSESSMENT',
  'EU_DECLARATION_OF_CONFORMITY_AND_CE_MARK',
  'FRIA_REQUIRED',
  // Defensive: not materialized by the current EU pack, but guards against
  // future pack changes. Harmless if the step never appears.
  'FRIA_NOT_REQUIRED',
  'DPIA_REQUIRED'
]);

export function RequirementListStep({
  stepId,
  stepKey,
  order,
  title,
  status,
  overview,
  locked,
  orderless = false,
  expiresAt,
  renewableAt,
  leadTime,
  leadTimeAcknowledgedAt,
  onContinue
}: RequirementListStepProps) {
  const dispatch = useAppDispatch();
  const { id: aiSystemId } = useParams<{ id: string }>();
  const { can, roleResolved } = useAuthorization();
  const [isHovered, setIsHovered] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [showSupportingDocModal, setShowSupportingDocModal] = useState(false);
  const [showConformityAssessmentModal, setShowConformityAssessmentModal] =
    useState(false);
  const [showFRIAOptionsModal, setShowFRIAOptionsModal] = useState(false);
  const [showDPIAOptionsModal, setShowDPIAOptionsModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [finishingCompliance, setFinishingCompliance] = useState(false);
  const [selectedOption, setSelectedOption] = useState('');
  const [selectedFRIAOption, setSelectedFRIAOption] = useState('');
  const [selectedDPIAOption, setSelectedDPIAOption] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [isFriaUploadFlow, setIsFriaUploadFlow] = useState(false);
  const [conformityRouteError, setConformityRouteError] = useState(false);
  const [isDpiaUploadFlow, setIsDpiaUploadFlow] = useState(false);

  const navigation = useNavigate();

  const { currentStepActions, actionsLoading, actionsError, steps } =
    useAppSelector((state) => state.requirementSteps);
  const {
    documentationReqBody,
    createAnnexIVDraftResponse,
    euDeclarationStatus
  } = useAppSelector((state) => state.documentation);
  const files = useAppSelector((state) => state.fileUpload);
  const isDisabled = locked && status === 'NOT_STARTED';
  const canManageRequirementList =
    roleResolved && can('aiSystems.requirementList.manage');
  const isStepActionDisabled = isDisabled || !canManageRequirementList;
  const isCheckboxStep = CHECKBOX_STEP_KEYS.has(stepKey);
  const isUploadStep = UPLOAD_STEP_KEYS.has(stepKey);
  const isRecordStep = RECORD_STEP_KEYS.has(stepKey);
  const isRecordCompleted = isRecordStep && status === 'COMPLETED';
  const [togglingActionId, setTogglingActionId] = useState<string | null>(null);
  // Optimistic value shown while the toggle API is in flight so the checkbox
  // reflects the click immediately (as a grey check, since it stays disabled),
  // then turns purple once the request resolves — matching the questionnaire UX.
  const [pendingToggle, setPendingToggle] = useState<{
    id: string;
    completed: boolean;
  } | null>(null);
  const [activeUploadActionKey, setActiveUploadActionKey] = useState<
    string | null
  >(null);
  const [evidenceUploadTarget, setEvidenceUploadTarget] =
    useState<EvidenceUploadTarget | null>(null);
  const [reopening, setReopening] = useState(false);
  const [reopenError, setReopenError] = useState<string | null>(null);

  // Compliance-record (final step) state.
  const [record, setRecord] = useState<ComplianceRecord | null>(null);
  const [recordLoading, setRecordLoading] = useState(false);
  const [recordError, setRecordError] = useState<string | null>(null);
  const [recordSubmitError, setRecordSubmitError] = useState<string | null>(null);
  const [submittingRecord, setSubmittingRecord] = useState(false);
  const [registeredTradeName, setRegisteredTradeName] = useState('');
  const [contactAddress, setContactAddress] = useState('');
  const [placementDate, setPlacementDate] = useState('');
  const [assessmentRoute, setAssessmentRoute] = useState<
    'internal' | 'external' | ''
  >('');
  const [downloadingRecord, setDownloadingRecord] = useState(false);

  const handleToggleAction = async (actionId: string, completed: boolean) => {
    if (!canManageRequirementList) return;
    try {
      setTogglingActionId(actionId);
      setPendingToggle({ id: actionId, completed });
      await dispatch(
        setRequirementActionCompleted({ stepId, actionId, completed })
      ).unwrap();
      // The toggled action's state is updated in place by the slice (no action
      // re-fetch, so the list doesn't blank). Only refresh the step list so its
      // status/lock — and the drawer pill fed from these props — reflect the
      // change; that refetch keeps the list visible.
      if (aiSystemId) {
        dispatch(fetchRequirementStepsByAISystemId(aiSystemId));
      }
    } catch (error) {
      console.error('Failed to toggle requirement action:', error);
    } finally {
      setTogglingActionId(null);
      setPendingToggle(null);
    }
  };
  const showButtonOnHover = status === 'COMPLETED' || status === 'NOT_STARTED';
  const config = getStatusConfig(status, locked);
  const expiry = getBiasAuditExpiryState(expiresAt, status);
  const leadTimeState = getNoticeLeadTimeState(leadTime, leadTimeAcknowledgedAt);

  // Expiry outranks the lead time: an expired obligation is not currently discharged at all, which
  // is the more urgent thing to say. A breached notice window is a permanent historical fact and
  // will still be there once the audit is renewed.
  const pillVariant: PillVariant =
    expiry.kind === 'expired'
      ? 'error'
      : expiry.kind === 'expiring-soon'
        ? 'warning'
        : leadTimeState.kind === 'breached' || leadTimeState.kind === 'no-use-date'
          ? 'warning'
          : leadTimeState.kind === 'acknowledged'
            ? // Closed, but not silently: 'neutral' records the accepted breach without implying
              // there is still something outstanding.
              'neutral'
            : config.pillVariant;

  const pillLabel =
    expiry.kind === 'expired'
      ? 'Attention Required'
      : expiry.kind === 'expiring-soon'
        ? 'Expiring soon'
        : leadTimeState.kind === 'breached'
          ? 'Notice too late'
          : leadTimeState.kind === 'no-use-date'
            ? 'First use date needed'
            : leadTimeState.kind === 'acknowledged'
              ? 'Late notice accepted'
              : config.label;

  // Only shown when the pack sets an expiry (e.g. the annual LL144 bias audit).
  // Gate on the FORMATTED value, not on `expiresAt`: formatDateOnly returns ''
  // for an unparseable date, and rendering the bare label would leave a dangling
  // "Expires " whose text collides with the pill label above.
  const expiresLabel = expiresAt ? formatDateOnly(expiresAt) : '';

  const expiryInfo = expiresLabel ? (
    <p
      className={`text-left text-xs font-normal ${
        expiry.kind === 'expired'
          ? 'text-rose-600'
          : expiry.kind === 'expiring-soon'
            ? 'text-amber-600'
            : 'text-gray-500'
      }`}
    >
      {expiry.kind === 'expired'
        ? 'Expired'
        : expiry.kind === 'expiring-soon'
          ? 'Expiring soon'
          : 'Expires'}{' '}
      {expiresLabel}
    </p>
  ) : null;

  // The drawer's first-use-date editor. '' means no date, which is also how the user asks for the
  // stored one to be cleared.
  const storedFirstUseDate = leadTime?.firstUseDate ?? '';
  const [firstUseDateDraft, setFirstUseDateDraft] = useState(storedFirstUseDate);
  const [savingFirstUseDate, setSavingFirstUseDate] = useState(false);
  const [firstUseDateError, setFirstUseDateError] = useState<string | null>(
    null
  );

  // Track the server value rather than seeding once. `useState` runs only on first mount, so
  // without this the field could show '' while a date is stored -- and since an empty field now
  // MEANS "clear", that stale blank plus the dirty check below would arm Save to wipe a date the
  // user never touched. Also what settles the button after a save: the refetch moves the stored
  // value, the draft follows, and the dirty check goes quiet.
  //
  // A background refetch does discard an in-progress edit. That is the right way round: losing a
  // few keystrokes beats silently clearing a stored date.
  useEffect(() => {
    setFirstUseDateDraft(storedFirstUseDate);
  }, [storedFirstUseDate]);

  const firstUseDateDirty = firstUseDateDraft !== storedFirstUseDate;

  const [acknowledging, setAcknowledging] = useState(false);
  const [acknowledgeError, setAcknowledgeError] = useState<string | null>(null);
  const [confirmingAcknowledge, setConfirmingAcknowledge] = useState(false);

  const handleAcknowledge = async (acknowledged: boolean) => {
    if (acknowledging) return;

    setAcknowledging(true);
    setAcknowledgeError(null);
    try {
      await acknowledgeLeadTime(stepId, acknowledged);
      // Refetch: accepting the breach completes the step and clears the system's attention state,
      // both decided server-side.
      if (aiSystemId) {
        dispatch(fetchRequirementStepsByAISystemId(aiSystemId));
      }
      setConfirmingAcknowledge(false);
    } catch (error) {
      console.error('Failed to update the lead-time acknowledgement:', error);
      setAcknowledgeError('Could not update the acknowledgement. Please try again.');
    } finally {
      setAcknowledging(false);
    }
  };

  const handleSaveFirstUseDate = async () => {
    if (!aiSystemId || !firstUseDateDirty || savingFirstUseDate) return;

    setSavingFirstUseDate(true);
    setFirstUseDateError(null);
    try {
      // `|| null` is the clear path: the endpoint takes null to mean "remove the date", and an
      // emptied field is how the user asks for it.
      await updateLl144FirstUseDate(aiSystemId, firstUseDateDraft || null);
      // Refetch rather than patching local state: the verdict is computed server-side, and
      // recomputing it here would be a second copy of the business-day rule.
      dispatch(fetchRequirementStepsByAISystemId(aiSystemId));
    } catch (error) {
      console.error('Failed to update the AEDT first use date:', error);
      setFirstUseDateError('Could not save the date. Please try again.');
    } finally {
      setSavingFirstUseDate(false);
    }
  };

  // Sits beside `expiryInfo`. Separate node rather than folded into it because a step can carry
  // both an expiry and a lead time, and they answer different questions.
  const leadTimeInfo =
    leadTimeState.kind === 'breached' ? (
      <p className="text-left text-xs font-normal text-amber-600">
        Notice given {leadTimeState.businessDaysGiven} of{' '}
        {leadTimeState.requiredBusinessDays} business days before first use
      </p>
    ) : leadTimeState.kind === 'no-use-date' ? (
      <p className="text-left text-xs font-normal text-amber-600">
        Add the date the tool was first used to check the{' '}
        {leadTimeState.requiredBusinessDays}-business-day notice window
      </p>
    ) : leadTimeState.kind === 'acknowledged' ? (
      // Deliberately still visible on a COMPLETED step. The obligation is closed, but it was closed
      // on an accepted breach, and presenting that as clean would misrepresent the record.
      <p className="text-left text-xs font-normal text-gray-500">
        Closed on an accepted late notice — given{' '}
        {leadTimeState.businessDaysGiven} of {leadTimeState.requiredBusinessDays} business days
        before first use
      </p>
    ) : null;

  const buttonTitleMap: Record<string, string> = {
    'Generate the initial draft of Annex IV document':
      'Generate Annex IV Draft',
    'Finalize the Annex IV documentation': 'Finalize and sign Annex IV',
    'Generate draft of EU Declaration of Conformity': 'Generate EU DoC Draft',
    'Finalize and sign declaration': 'Finalize and sign EU DoC',
    'Acquired CE Mark': 'Finalize and sign EU DoC'
  };

  const buttonTitle = useMemo(() => {
    const currentStepAction = currentStepActions.find(
      (action) => action.completed === false
    );

    if (!currentStepAction) return '';
    return buttonTitleMap[currentStepAction.description];
  }, [currentStepActions]);

  useEffect(() => {
    if (stepKey === 'EU_DECLARATION_OF_CONFORMITY_AND_CE_MARK') {
      if (euDeclarationStatus === 'COMPLETED' && status !== 'COMPLETED') {
        return;
      }
      dispatch(setEUDeclarationStatus(status));
    }
  }, [dispatch, euDeclarationStatus, status, stepKey]);

  const getActionButton = (stepKey: string) => {
    const baseUrl = window.location.pathname.split('/requirement-list')[0];
    switch (stepKey) {
      case 'CONNECT_REPOSITORY':
        return {
          icon: Plus(),
          title: 'Connect to Repository',
          action: () => {
            navigation(`${baseUrl}/repository-management`);
            dispatch(setFromRequirement(true));
          }
        };
      case 'SUPPORTING_DOCUMENTATION':
        return {
          icon: UploadIcon('#111827', 16),
          title: 'Upload Documents',
          action: () => {
            setSelectedOption('');
            setIsFriaUploadFlow(false);
            setShowSupportingDocModal(true);
            setIsDrawerOpen(false);
          },
          actionComplete: () => {
            setShowCompleteModal(true);
            setIsDrawerOpen(false);
          }
        };
      case 'ANNEX_IV':
        return {
          icon: PencilIcon(),
          title: buttonTitle,
          action: async () => {
            await handleCreateAnnexIVDraft();
            setIsDrawerOpen(false);
          }
        };
      case 'CONFORMITY_ASSESSMENT':
        return {
          icon: UploadIcon('#111827', 16),
          title: 'Upload Documents',
          action: () => {
            setSelectedOption('');
            setShowConformityAssessmentModal(true);
            setIsDrawerOpen(false);
          }
        };
      case 'EU_DECLARATION_OF_CONFORMITY_AND_CE_MARK':
        return {
          icon: PencilIcon(),
          title: buttonTitle,
          action: async () => {
            await handleGenerateEUDeclaration();
            setIsDrawerOpen(false);
          }
        };
      case 'FRIA_REQUIRED':
        return {
          icon: Plus(),
          title: 'Provide FRIA Document',
          action: async () => {
            setIsDrawerOpen(false);
            await handleOpenExistingFRIADraftOrShowOptions();
          }
        };
      case 'DPIA_REQUIRED':
        return {
          icon: Plus(),
          title: 'Provide DPIA Document',
          action: async () => {
            setIsDrawerOpen(false);
            await handleOpenExistingDPIADraftOrShowOptions();
          }
        };

      default:
        return {
          icon: '',
          action: () => {}
        };
    }
  };
  const actionButton = getActionButton(stepKey);
  const showCompleteAction = actionButton.actionComplete && steps.length === 2;

  // Pack-driven (non-EU) actions carry an `evidenceKind` and use the generic
  // inline evidence controls instead of the EU-specific action button. EU steps
  // keep their bespoke generator flows even though their actions also carry an
  // evidenceKind now.
  const hasGenericEvidence =
    !EU_SPECIAL_STEP_KEYS.has(stepKey) &&
    currentStepActions.some((action) => !!action.evidenceKind);

  // Mirrors the server's 409 guard exactly (`assertStepAcceptsNewEvidence`), which is why the
  // boundary comes from the server as `renewableAt` rather than being recomputed from
  // EXPIRING_SOON_DAYS: the badge uses a strict `<` on a 30-day delta while the guard is inclusive
  // `>=`, so reusing it here would desync the control from the API by a tick.
  //
  // Accepts when the step is not COMPLETED (covers NOT_STARTED, IN_PROGRESS and EXPIRED, since
  // `status` is already the server's effective status), when it carries no expiry at all, or once
  // the renewal window has opened. Gating on the STEP's status rather than `action.completed` also
  // keeps the control visible on a partially-complete multi-action step — which is what the server
  // does too.
  const isRenewable =
    Boolean(renewableAt) && new Date(renewableAt!).getTime() <= Date.now();
  const acceptsNewEvidence =
    !locked && (status !== 'COMPLETED' || !expiresAt || isRenewable);

  // A pinned footer button can only target one action, so hoist only when the step has a single
  // document action (today: LL144_BIAS_AUDIT). A step mixing kinds — e.g.
  // LL144_CANDIDATE_NOTICE's document + url — keeps its controls inline, beside the descriptions
  // they belong to.
  const footerEvidenceAction =
    hasGenericEvidence &&
    currentStepActions.length === 1 &&
    currentStepActions[0].evidenceKind === 'document'
      ? currentStepActions[0]
      : undefined;

  // The step-kind exclusions mirror the gate this is ORed into on `showActionButton`: those
  // layouts render their own per-action controls (checkbox ticks, per-document uploads) or own the
  // footer outright (the compliance record), so a hoisted button there would be a second, duplicate
  // affordance. Unreachable today — importer/distributor steps are code constants with no evidence
  // block, so `hasGenericEvidence` is already false for them — but cheap to keep honest.
  const showFooterEvidenceUpload = Boolean(
    footerEvidenceAction &&
      aiSystemId &&
      canManageRequirementList &&
      acceptsNewEvidence &&
      !actionsLoading &&
      !isCheckboxStep &&
      !isUploadStep &&
      !isRecordStep
  );

  // The only suppressed case: satisfied, recurring, and not yet renewable. A completed step with no
  // expiry stays re-submittable — the harm this guards against comes from the pack's `invalidates`
  // edges, which only the recurring bias-audit action carries.
  //
  // The dateless wording is the fallback for an API that predates `renewableAt`: the control is
  // still correctly hidden, and saying so beats rendering an empty drawer.
  const renewalNotice =
    hasGenericEvidence && !acceptsNewEvidence
      ? renewableAt
        ? `Completed — renewal opens ${formatDateOnly(renewableAt)}.`
        : 'Completed — not yet due for renewal.'
      : undefined;

  const handleEvidenceSubmitted = () => {
    if (aiSystemId) {
      dispatch(fetchStepActionsByStepId(stepId));
      dispatch(fetchRequirementStepsByAISystemId(aiSystemId));
    }
    dispatch(
      showGlobalToast({ message: 'Evidence submitted.', type: 'success' })
    );
    setIsDrawerOpen(false);
  };

  // Document evidence uploads through a Modal, which must be a sibling of the SideDrawer rather
  // than a child of it (Modal is z-10 against the drawer backdrop's z-40, and SideDrawer returns
  // null when closed). So close the drawer while the dialog is up and reopen it afterwards — the
  // same dance the importer and FRIA/DPIA uploads already do.
  // `applyActionInvalidations` runs on EVERY evidence submission, not only renewals — so gating this
  // on the renewal window was wrong twice over: a replacement after Reopen (which clears `expiresAt`,
  // so nothing looks renewable) and a first upload by someone who published the results URL before
  // uploading any audit both destroyed a completed step's evidence silently.
  //
  // Warn on the real condition instead: a step this action reopens that actually has something to
  // lose. `status !== 'NOT_STARTED'` rather than `=== 'COMPLETED'` because the reset deletes ALL of
  // the target's evidence and un-completes ALL its actions, so a partially-filled or expired step
  // loses work too.
  const buildResetWarning = (action: StepAction): string | undefined => {
    // Absent (an API predating the field) is not the same as empty: fall back to generic wording
    // rather than staying silent about a reset we simply cannot see.
    if (!action.invalidatesStepKeys) {
      return 'Submitting new evidence may reopen dependent obligations — their submitted evidence would be cleared and need to be provided again.';
    }
    const atRisk = steps.filter(
      (candidate) =>
        action.invalidatesStepKeys!.includes(candidate.key) &&
        candidate.status !== 'NOT_STARTED'
    );
    if (atRisk.length === 0) return undefined;
    const names = atRisk.map((candidate) => candidate.title).join(', ');
    return `Submitting this will reopen ${names} — its submitted evidence will be cleared and will need to be provided again.`;
  };

  const handleRequestEvidenceUpload = (action: StepAction) => {
    setEvidenceUploadTarget({
      actionId: action.id,
      description: action.description,
      evidenceConfig: action.evidenceConfig,
      resetWarning: buildResetWarning(action)
    });
    setIsDrawerOpen(false);
  };

  const closeEvidenceUpload = () => {
    setEvidenceUploadTarget(null);
    setIsDrawerOpen(true);
  };

  const handleEvidenceUploadSubmitted = () => {
    setEvidenceUploadTarget(null);
    handleEvidenceSubmitted();
  };

  // The server refused because the step is already satisfied — our view was stale. Resync and put
  // the user back in the drawer, where the renewal notice and the reopen route are.
  const handleEvidenceAlreadySatisfied = (message: string) => {
    setEvidenceUploadTarget(null);
    if (aiSystemId) {
      dispatch(fetchStepActionsByStepId(stepId));
      dispatch(fetchRequirementStepsByAISystemId(aiSystemId));
    }
    dispatch(showGlobalToast({ message, type: 'error' }));
    setIsDrawerOpen(true);
  };

  // The escape hatch for a wrong upload inside the locked window: un-complete the action, which
  // clears the step's completedAt/expiresAt server-side and so re-admits evidence through the
  // guard's "not COMPLETED" branch.
  //
  // Deliberately NOT called "replace": `markActionUncompleted` does not delete evidence rows, so the
  // superseded document stays in the history. That is intended — an audit trail must not silently
  // drop a submitted compliance document, and one mis-click should not destroy one.
  const handleReopenForNewEvidence = async () => {
    if (!canManageRequirementList || reopening) return;
    const target = currentStepActions.find((action) => action.completed);
    if (!target) return;

    setReopening(true);
    setReopenError(null);
    try {
      await dispatch(
        setRequirementActionCompleted({
          stepId,
          actionId: target.id,
          completed: false
        })
      ).unwrap();
      dispatch(fetchStepActionsByStepId(stepId));
      if (aiSystemId) {
        dispatch(fetchRequirementStepsByAISystemId(aiSystemId));
      }
    } catch (error) {
      console.error('Failed to reopen requirement action:', error);
      setReopenError('Could not reopen this task. Please try again.');
    } finally {
      setReopening(false);
    }
  };

  const getUploadCategory = () => {
    if (
      selectedOption === 'Internal Assessment' ||
      selectedOption === 'External Assessment'
    ) {
      return 'CONFORMITY_ASSESSMENT';
    }
    return 'OTHER';
  };

  const getConformityAssessmentType = ():
    | 'INTERNAL'
    | 'EXTERNAL'
    | undefined => {
    if (selectedOption === 'Internal Assessment') {
      return 'INTERNAL';
    }
    if (selectedOption === 'External Assessment') {
      return 'EXTERNAL';
    }
    return undefined;
  };

  const uploadLimits = isFriaUploadFlow
    ? uploadFileLimits.other
    : isDpiaUploadFlow
      ? uploadFileLimits.other
      : selectedOption.trim() === ''
        ? uploadFileLimits.default
        : selectedOption.trim() === 'Internal Assessment'
          ? uploadFileLimits.internal
          : uploadFileLimits.other;

  const { uploadFiles, uploading } = useDocumentUpload({
    aiSystemId: aiSystemId || '',
    pathPrefix: 'user-uploads',
    onSuccess: () => {
      // Importer per-document uploads happen from inside the (temporarily
      // closed) drawer; reopen it afterwards so the user sees the updated
      // checkmarks and can upload the next document.
      const wasImporterUpload = activeUploadActionKey !== null;
      setShowSupportingDocModal(false);
      setActiveUploadActionKey(null);
      setUploadedCount(files.length);
      setShowToast(true);
      dispatch(resetFileState());
      // Refresh the open drawer's action states (e.g. importer per-document
      // uploads auto-complete their action) and the step list.
      dispatch(fetchStepActionsByStepId(stepId));
      if (aiSystemId) {
        dispatch(fetchRequirementStepsByAISystemId(aiSystemId));
      }
      if (wasImporterUpload) {
        setIsDrawerOpen(true);
      }
    },
    onError: (error) => {
      console.error('Upload error:', error);
      // Uploads pass skipErrorToast, so surface the reason ourselves. The
      // hook builds this message from the per-file failure reasons.
      dispatch(
        showGlobalToast({
          message: error.message || 'Failed to upload. Please try again.',
          type: 'error'
        })
      );
      // Intentionally keep the selected files in redux so the user can
      // correct the issue and retry; resetFileState() runs only on success.
    }
  });

  const handleCreateAnnexIVDraft = async () => {
    try {
      const response = await createAnnexIVDraft(
        aiSystemId || documentationReqBody.aiSystemId
      );
      dispatch(setCreateAnnexIVDraftResponse(response));
      dispatch(setAnnexIVFieldsState(mapAnnexContentToFields(response)));
      dispatch(handleOpenSidePanel(true));
      dispatch(setSidePanelTitle('Annex IV - Technical Documentation (Draft)'));
      dispatch(setSidePanelContent('ANNEX_IV'));
    } catch (error) {
      console.error('Error creating AnnexIV draft', error);
    }
  };

  const handleGenerateEUDeclaration = async () => {
    try {
      const aiSysId = aiSystemId || documentationReqBody.aiSystemId;
      let response;

      try {
        response = await getEUDeclarationByAiSystem(aiSysId, {
          suppressErrorToast: true
        });
      } catch (error: any) {
        if (error.response?.status === 404) {
          response = await createEUDeclarationDraft(
            aiSysId,
            createAnnexIVDraftResponse?.id
          );
        } else {
          throw error;
        }
      }

      dispatch(setCreateEUDeclarationResponse(response));
      dispatch(
        setEUDeclarationFieldsState(mapEUDeclarationContentToFields(response))
      );
      dispatch(setSidePanelTitle('EU Declaration of Conformity (Draft)'));
      dispatch(setSidePanelContent('EU_DECLARATION'));
      dispatch(handleOpenSidePanel(true));
    } catch (error: any) {
      console.error('Error creating EU Declaration draft', error);
      console.error('Error response:', error.response?.data);
    }
  };

  const handleGenerateFRIA = async () => {
    try {
      const aiSysId = aiSystemId || documentationReqBody.aiSystemId;

      // Try to get existing FRIA draft first
      let response: Awaited<ReturnType<typeof getFRIAByAiSystem>> | null = null;
      try {
        response = await getFRIAByAiSystem(aiSysId, {
          suppressErrorToast: true
        });
        console.log('Found existing FRIA draft:', response);
      } catch (error: any) {
        // If not found (404), create a new draft
        if (error.response?.status === 404) {
          console.log('No existing FRIA found, creating new draft');
          response = await createFRIADraft(aiSysId);
        } else {
          throw error;
        }
      }

      if (response) {
        dispatch(setCreateFRIAResponse(response));
        dispatch(setFRIAFieldsState(mapFRIAContentToFields(response)));
        dispatch(setSidePanelTitle('FRIA Document (Draft)'));
        dispatch(setSidePanelContent('FRIA'));
        dispatch(handleOpenSidePanel(true));
      }
    } catch (error: any) {
      console.error('Error creating FRIA draft', error);
      console.error('Error response:', error.response?.data);
    }
  };

  const handleOpenExistingFRIADraftOrShowOptions = async () => {
    const aiSysId = aiSystemId || documentationReqBody.aiSystemId;
    if (!aiSysId) {
      setSelectedFRIAOption('');
      setShowFRIAOptionsModal(true);
      return;
    }

    try {
      const response = await getFRIAByAiSystem(aiSysId, {
        suppressErrorToast: true
      });

      dispatch(setCreateFRIAResponse(response));
      dispatch(setFRIAFieldsState(mapFRIAContentToFields(response)));
      dispatch(setSidePanelTitle('FRIA Document (Draft)'));
      dispatch(setSidePanelContent('FRIA'));
      dispatch(handleOpenSidePanel(true));
      return;
    } catch (error: any) {
      // If draft does not exist (404), user should choose Generate or Upload.
      if (error?.response?.status !== 404) {
        console.error('Error loading existing FRIA draft', error);
      }
    }

    setSelectedFRIAOption('');
    setShowFRIAOptionsModal(true);
  };

  const handleFriaOptionsNextClick = async () => {
    setShowFRIAOptionsModal(false);

    if (selectedFRIAOption === 'Generate FRIA') {
      await handleGenerateFRIA();
    } else if (selectedFRIAOption === 'Upload FRIA') {
      setSelectedOption('');
      setIsFriaUploadFlow(true);
      setShowSupportingDocModal(true);
    }

    setSelectedFRIAOption('');
  };

  const handleDpiaOptionsNextClick = async () => {
    setShowDPIAOptionsModal(false);

    if (selectedDPIAOption === 'Generate DPIA') {
      await handleGenerateDPIA();
    } else if (selectedDPIAOption === 'Upload DPIA') {
      setSelectedOption('');
      setIsDpiaUploadFlow(true);
      setShowSupportingDocModal(true);
    }

    setSelectedDPIAOption('');
  };

  const handleOpenExistingDPIADraftOrShowOptions = async () => {
    const aiSysId = aiSystemId || documentationReqBody.aiSystemId;
    if (!aiSysId) {
      setSelectedDPIAOption('');
      setShowDPIAOptionsModal(true);
      return;
    }

    try {
      const response = await getDPIAByAiSystem(aiSysId, {
        suppressErrorToast: true
      });

      dispatch(setCreateDPIAResponse(response));
      dispatch(setDPIAFieldsState(mapDPIAContentToFields(response)));
      dispatch(setSidePanelTitle('DPIA Document (Draft)'));
      dispatch(setSidePanelContent('DPIA'));
      dispatch(handleOpenSidePanel(true));
      return;
    } catch (error: any) {
      // If draft does not exist (404), user should choose Generate or Upload.
      if (error?.response?.status !== 404) {
        console.error('Error loading existing DPIA draft', error);
      }
    }

    setSelectedDPIAOption('');
    setShowDPIAOptionsModal(true);
  };

  const handleGenerateDPIA = async () => {
    try {
      const aiSysId = aiSystemId || documentationReqBody.aiSystemId;
      let response;

      try {
        // Try to get existing DPIA draft first
        response = await getDPIAByAiSystem(aiSysId, {
          suppressErrorToast: true
        });
      } catch (error: any) {
        if (error.response?.status === 404) {
          console.error('No existing DPIA found for AI System ID:', aiSysId);
          // If not found (404), create a new draft
          response = await createDPIADraft(aiSysId);
        } else {
          throw error;
        }
      }

      dispatch(setCreateDPIAResponse(response));
      dispatch(setDPIAFieldsState(mapDPIAContentToFields(response)));
      dispatch(setSidePanelTitle('DPIA Document (Draft)'));
      dispatch(setSidePanelContent('DPIA'));
      dispatch(handleOpenSidePanel(true));
    } catch (error) {
      console.error('Error creating DPIA draft', error);
    }
  };

  const handleCompleteButton = async () => {
    if (!aiSystemId) return;

    setFinishingCompliance(true);
    try {
      await finishCompliance(aiSystemId);
      setShowCompleteModal(false);
      dispatch(fetchRequirementStepsByAISystemId(aiSystemId));
    } catch (error) {
      console.error('Error finishing compliance:', error);
    } finally {
      setFinishingCompliance(false);
    }
  };

  const hasInternalAssessmentDocument = async (
    aiSystemId: string
  ): Promise<boolean> => {
    let offset = 0;
    let total = 0;

    do {
      const response = await getDocumentation({
        ...documentationReqBody,
        aiSystemId,
        offset
      });

      const foundDocument = response.items.some(
        (doc) =>
          doc.category === 'CONFORMITY_ASSESSMENT' &&
          doc.conformityAssessmentType === 'INTERNAL'
      );

      if (foundDocument) return true;

      total = response.total;
      offset += response.items.length;
    } while (offset < total);

    return false;
  };

  const handleConformityNextClick = async () => {
    setConformityRouteError(false);

    const aiSysId = aiSystemId || documentationReqBody.aiSystemId;
    if (!aiSysId) return;

    if (selectedOption === 'External Assessment') {
      const hasInternal = await hasInternalAssessmentDocument(aiSysId);

      if (hasInternal) {
        setConformityRouteError(true);
        return;
      }
    }

    setIsFriaUploadFlow(false);
    setShowSupportingDocModal(true);
    setShowConformityAssessmentModal(false);
  };

  const loadComplianceRecord = async () => {
    if (!aiSystemId) return;
    setRecordLoading(true);
    setRecordError(null);
    setRecordSubmitError(null);
    try {
      // A completed step is read-only: fetch the signed record, never create
      // (creating on a completed step used to spawn a duplicate empty draft).
      const rec = isRecordCompleted
        ? await getComplianceRecord(aiSystemId)
        : await createComplianceRecord(aiSystemId); // get-or-create
      setRecord(rec);
      if (rec.importer) {
        setRegisteredTradeName(rec.importer.registeredTradeName ?? '');
        setContactAddress(rec.importer.contactAddress ?? '');
        setPlacementDate(rec.importer.placementDate ?? '');
        setAssessmentRoute(rec.importer.assessmentRoute ?? '');
      }
    } catch (error) {
      console.error('Failed to load compliance record:', error);
      setRecordError('Failed to load the compliance record.');
    } finally {
      setRecordLoading(false);
    }
  };

  const handleGenerateAndSign = async () => {
    if (!aiSystemId || !record || submittingRecord) return;

    // Importer records require the identity/retention fields. Validate up front
    // with a friendly message so the user never sees the raw backend date-format
    // error when the market placement date (or another field) is left empty.
    if (record.importer) {
      const missing: string[] = [];
      if (!registeredTradeName.trim())
        missing.push('registered trade name / trade mark');
      if (!contactAddress.trim()) missing.push('contact address');
      if (!placementDate.trim()) missing.push('market placement date');
      if (!assessmentRoute) missing.push('conformity assessment route');
      if (missing.length > 0) {
        setRecordSubmitError(
          `Please fill in the ${missing.join(', ')} before signing.`
        );
        return;
      }
    }

    setSubmittingRecord(true);
    setRecordSubmitError(null);
    try {
      const payload = record.importer
        ? {
            registeredTradeName,
            contactAddress,
            placementDate,
            assessmentRoute: assessmentRoute || undefined
          }
        : {};
      const result = await completeComplianceRecord(aiSystemId, payload);

      if (!result.success) {
        setRecordSubmitError(
          result.precedingStepsIncomplete
            ? 'Complete all previous steps before generating and signing this record.'
            : result.missingCertificate
              ? 'This system used the external (notified-body) assessment route — upload the notified-body certificate in Step 1 before signing.'
              : 'Please fill all required fields before signing.'
        );
        setSubmittingRecord(false);
        return;
      }

      if (result.requiresSignature) {
        const returnUrl = `${window.location.origin}/ai-systems/${aiSystemId}/requirement-list`;
        const signingUrl = await signDocument({
          documentId: record.documentId,
          returnUrl
        });
        window.location.assign(signingUrl); // navigate to the signing ceremony
        return;
      }

      // No-signature fallback (SignatureAPI disabled): still call signDocument so
      // the backend renders the PDF and finalizes the record (no ceremony/redirect,
      // no webhook). Mirrors the provider flow's fallback. Await both refreshes so
      // the requirement list step status and the record itself are up to date
      // before the drawer closes - otherwise they still show as incomplete until
      // a manual page reload. Fetch the record directly with getComplianceRecord
      // (not loadComplianceRecord's isRecordCompleted branch, which still reads the
      // stale pre-refresh status prop and would wrongly create a duplicate draft).
      await signDocument({
        documentId: record.documentId,
        returnUrl: window.location.href
      });
      await dispatch(fetchRequirementStepsByAISystemId(aiSystemId));
      const refreshedRecord = await getComplianceRecord(aiSystemId);
      setRecord(refreshedRecord);
      setIsDrawerOpen(false);
    } catch (error) {
      console.error('Failed to generate & sign compliance record:', error);
      setRecordSubmitError('Failed to submit the record. Please try again.');
    } finally {
      setSubmittingRecord(false);
    }
  };

  // Download the signed record straight from the drawer (completed steps only),
  // mirroring the Documentation tab's download: fetch the presigned URL, then
  // click a synthetic anchor.
  const handleDownloadRecord = async () => {
    if (!record || downloadingRecord) return;
    setDownloadingRecord(true);
    try {
      const docData = await downloadDocument(record.documentId);
      if (docData.downloadUrl) {
        const link = document.createElement('a');
        link.href = docData.downloadUrl;
        link.download = docData.fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Failed to download compliance record:', error);
    } finally {
      setDownloadingRecord(false);
    }
  };

  const handleButtonClick = async () => {
    if (!canManageRequirementList) {
      return;
    }

    setIsDrawerOpen(true);
    dispatch(fetchStepActionsByStepId(stepId));

    if (isRecordStep) {
      void loadComplianceRecord();
    }

    if (onContinue) {
      onContinue();
    }
  };

  const getCircleStyle = (actionComplete: boolean) => {
    if (actionComplete) {
      return <div className="min-w-[22px]">{CheckCircle('#717AE3', 22)}</div>;
    } else {
      return <div className="min-w-[22px]">{EmptyCircle('#767681', 16)}</div>;
    }
  };

  return (
    <div className="flex gap-4 items-stretch group">
      {/* Number Badge - Outside the card */}
      <div className="flex flex-col items-center gap-2 relative">
        {status === 'COMPLETED' ? (
          <div className="w-6 h-6 flex items-center justify-center bg-purple-600 rounded-full">
            {CheckmarkIcon()}
          </div>
        ) : (
          <div
            className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${config.numberClass}`}
          >
            {orderless ? null : order}
          </div>
        )}
        {/* Gray Line - the visual "flows into" cue; an orderless pack has none. */}
        {!orderless && (
          <div
            data-testid="step-connector"
            className="w-[2px] flex-1 bg-gray-250v2"
          ></div>
        )}
      </div>

      {/* Card Content */}
      <div
        data-testid="requirement-step-card"
        className={`relative flex-1 flex flex-col gap-2 p-4 ${
          status === 'IN_PROGRESS' ? '' : 'pb-[11px]'
        } border ${getBorderClasses(
          status,
          locked,
          expiry.kind,
          leadTimeState.kind
        )} rounded-lg transition-all`}
      >
        {/* Header with Title and Status */}
        <div className="flex items-start justify-between gap-4">
          <h3
            className={`text-base flex gap-1 font-semibold ${
              locked ? 'text-gray-800' : 'text-gray-900'
            }`}
          >
            {title}
            {/* Lock indicator for locked steps */}
            {isDisabled && (
              <Tooltip
                content="Complete previous steps to unlock"
                position="bottom"
                showArrow={false}
                delay={100}
              >
                <div
                  className="w-6 h-6 flex items-center justify-center bg-gray-100 rounded-full hover:bg-gray-900v2"
                  onMouseEnter={() => {
                    setIsHovered(true);
                  }}
                  onMouseLeave={() => {
                    setIsHovered(false);
                  }}
                >
                  {LockIcon(isHovered ? 'white' : '#4E4E56')}
                </div>
              </Tooltip>
            )}
          </h3>
          <Pill variant={pillVariant}>{pillLabel}</Pill>
        </div>

        {/* Content Container */}
        <div className="flex gap-2 justify-between">
          {/* Description */}
          <p
            className={`text-sm font-normal text-left ${
              locked ? 'text-gray-550' : 'text-gray-800'
            }`}
          >
            {overview}
          </p>

          {/* Action Button */}
          {config.showButton && (
            <div
              className={`flex justify-end mt-auto min-w-[150px] h-10 ${
                showButtonOnHover
                  ? 'opacity-0 group-hover:opacity-100 transition-opacity'
                  : ''
              }`}
            >
              <TextButton
                onClick={handleButtonClick}
                disabled={isStepActionDisabled}
                className="text-purple-800 break-keep"
                icon={RightArrow()}
                iconPosition="right"
                size="xs"
              >
                {config.buttonText}
              </TextButton>
            </div>
          )}
        </div>

        {/* Expiry (only when the step has an expiry date) */}
        {expiryInfo}
        {leadTimeInfo}
      </div>

      {/* Side Drawer */}
      <SideDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        title={orderless ? title : `Step ${order}: ${title}`}
        size={isRecordStep ? 'wide' : 'default'}
        headerAction={
          isRecordStep && isRecordCompleted && record ? (
            <button
              onClick={handleDownloadRecord}
              disabled={downloadingRecord}
              className="p-1 rounded text-purple-800 hover:bg-gray-100 transition-colors disabled:opacity-50"
              aria-label="Download signed record"
              title="Download signed record"
            >
              {DownloadIcon()}
            </button>
          ) : undefined
        }
        status={<Pill variant={pillVariant}>{pillLabel}</Pill>}
        showActionButton={
          showFooterEvidenceUpload ||
          ((config.label === 'In Progress' || config.label === 'Not Started') &&
            !locked &&
            canManageRequirementList &&
            !isCheckboxStep &&
            !isUploadStep)
        }
        locked={locked}
        overview={
          <div className="flex flex-col gap-1">
            <p className="text-sm text-gray-900v2 leading-[20px]">{overview}</p>
            {expiryInfo}
            {leadTimeInfo}
          </div>
        }
        requiredActions={
          isRecordStep ? (
            recordLoading ? (
              <div className="text-sm text-gray-500">Loading record...</div>
            ) : recordError ? (
              <div className="text-sm text-red-600">{recordError}</div>
            ) : record ? (
              <ComplianceRecordDrawerForm
                record={record}
                registeredTradeName={registeredTradeName}
                contactAddress={contactAddress}
                placementDate={placementDate}
                assessmentRoute={assessmentRoute}
                onChangeRegisteredTradeName={setRegisteredTradeName}
                onChangeContactAddress={setContactAddress}
                onChangePlacementDate={setPlacementDate}
                onChangeAssessmentRoute={setAssessmentRoute}
                disabled={!canManageRequirementList || isRecordCompleted}
                renderCircle={getCircleStyle}
              />
            ) : (
              <div className="text-sm text-gray-500">No record available</div>
            )
          ) : actionsLoading ? (
            <div className="text-sm text-gray-500">Loading actions...</div>
          ) : actionsError ? (
            <div className="text-sm text-red-600">Error: {actionsError}</div>
          ) : currentStepActions.length > 0 ? (
            isCheckboxStep ? (
              <ul className="space-y-3">
                {currentStepActions.map((action) => (
                  <li key={action.id} className="flex items-start">
                    <CheckboxItem
                      label={action.description}
                      value={action.id}
                      checked={
                        pendingToggle?.id === action.id
                          ? pendingToggle.completed
                          : action.completed
                      }
                      disabled={
                        locked ||
                        !canManageRequirementList ||
                        togglingActionId === action.id
                      }
                      labelClasses="flex items-start gap-2"
                      textClasses="text-sm"
                      onChange={(_value, checked) =>
                        handleToggleAction(action.id, checked)
                      }
                    />
                  </li>
                ))}
              </ul>
            ) : isUploadStep ? (
              <ul className="space-y-3">
                {currentStepActions.map((action) => (
                  <li
                    key={action.id}
                    className="flex items-start justify-between gap-2"
                  >
                    <div className="flex items-start gap-1 text-gray-550">
                      {getCircleStyle(action.completed)}
                      <span className="text-sm">{action.description}</span>
                    </div>
                    {canManageRequirementList && (
                      <TextButton
                        size="xs"
                        icon={UploadIcon('#111827', 16)}
                        className="text-purple-800 break-keep"
                        onClick={() => {
                          setActiveUploadActionKey(action.key);
                          setShowSupportingDocModal(true);
                          // Close the drawer so its backdrop (z-40) doesn't dim
                          // the upload modal (z-10); it reopens on success/cancel.
                          setIsDrawerOpen(false);
                        }}
                      >
                        {action.completed ? 'Replace' : 'Upload'}
                      </TextButton>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <ul className="space-y-3">
                {/* Gated on the VERDICT, not on `leadTimeState` -- a satisfied window maps to
                    kind 'none' so that the card stays undecorated, and gating the editor on that
                    hid it exactly when a wrong-but-currently-passing date needs correcting. */}
                {leadTimeState.kind === 'breached' && canManageRequirementList && (
                  <li className="flex flex-col gap-2">
                    <AttentionMessage
                      message={
                        <p className="text-rose-700 text-xs leading-4">
                          <span className="font-medium mr-1">
                            Notice window missed:
                          </span>
                          this obligation stays open and the system is flagged
                          until the dates are corrected, or the breach is
                          accepted.
                        </p>
                      }
                      className="bg-rose-100 flex items-start justify-between gap-2 py-2 pl-2 pr-4 rounded-md"
                    />
                    {confirmingAcknowledge ? (
                      <div className="flex flex-col gap-2">
                        {/* A late notice cannot be un-sent, so this is not a fix — it is a record
                            that someone decided to accept it. Worth one deliberate confirmation. */}
                        <p className="text-sm text-gray-600 text-left">
                          Accepting keeps the breach on record and in the audit
                          log, attributed to you. It stops the breach holding
                          this obligation and the system open.
                        </p>
                        <div className="flex items-center gap-3">
                          <TextButton
                            size="xs"
                            className="text-purple-800 break-keep"
                            onClick={() => handleAcknowledge(true)}
                            disabled={acknowledging}
                          >
                            {acknowledging ? 'Saving...' : 'Accept the breach'}
                          </TextButton>
                          <TextButton
                            size="xs"
                            className="text-gray-500 break-keep"
                            onClick={() => setConfirmingAcknowledge(false)}
                            disabled={acknowledging}
                          >
                            Cancel
                          </TextButton>
                        </div>
                      </div>
                    ) : (
                      <div className="flex justify-start">
                        <TextButton
                          size="xs"
                          className="text-purple-800 break-keep"
                          onClick={() => setConfirmingAcknowledge(true)}
                        >
                          Accept the late notice
                        </TextButton>
                      </div>
                    )}
                    {acknowledgeError && (
                      <span className="text-sm text-red-600 text-left">
                        {acknowledgeError}
                      </span>
                    )}
                  </li>
                )}
                {leadTimeState.kind === 'acknowledged' && canManageRequirementList && (
                  <li className="flex flex-col gap-2">
                    <p className="text-sm text-gray-600 text-left">
                      The late notice on this obligation was accepted. The
                      breach stays on record; withdrawing reopens the obligation
                      and flags the system again.
                    </p>
                    <div className="flex justify-start">
                      <TextButton
                        size="xs"
                        className="text-purple-800 break-keep"
                        onClick={() => handleAcknowledge(false)}
                        disabled={acknowledging}
                      >
                        {acknowledging ? 'Saving...' : 'Withdraw acceptance'}
                      </TextButton>
                    </div>
                    {acknowledgeError && (
                      <span className="text-sm text-red-600 text-left">
                        {acknowledgeError}
                      </span>
                    )}
                  </li>
                )}
                {leadTime && canManageRequirementList && (
                  <li className="flex flex-col gap-2">
                    <p className="text-sm text-gray-600 text-left">
                      {leadTimeState.kind === 'breached'
                        ? `The notice was given ${leadTimeState.businessDaysGiven} of the required ${leadTimeState.requiredBusinessDays} business days before the tool was first used. Correct the first use date below if it is wrong.`
                        : leadTimeState.kind === 'no-use-date'
                          ? 'Record when the tool is first used to assess candidates or employees, so the notice window can be checked.'
                          : `The notice cleared the required ${leadTime.requiredBusinessDays} business days. Update the first use date if it is wrong, or clear it to remove the record.`}
                    </p>
                    <div className="flex items-end gap-2">
                      <Input
                        id={`aedt-first-use-date-${stepId}`}
                        type="date"
                        label="AEDT first use date"
                        value={firstUseDateDraft}
                        maxLength={10}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => setFirstUseDateDraft(event.target.value)}
                        containerClassName="w-48"
                      />
                      <TextButton
                        size="xs"
                        className="text-purple-800 break-keep mb-2"
                        onClick={handleSaveFirstUseDate}
                        disabled={savingFirstUseDate || !firstUseDateDirty}
                      >
                        {savingFirstUseDate ? 'Saving...' : 'Save'}
                      </TextButton>
                    </div>
                    {firstUseDateError && (
                      <span className="text-sm text-red-600 text-left">
                        {firstUseDateError}
                      </span>
                    )}
                  </li>
                )}
                {renewalNotice && (
                  <li className="flex flex-col gap-2">
                    <p className="text-sm text-gray-600 text-left">
                      {renewalNotice}
                    </p>
                    {canManageRequirementList && (
                      <div className="flex justify-start">
                        <TextButton
                          size="xs"
                          className="text-purple-800 break-keep"
                          onClick={handleReopenForNewEvidence}
                          disabled={reopening}
                        >
                          {reopening
                            ? 'Reopening...'
                            : 'Reopen to submit new evidence'}
                        </TextButton>
                      </div>
                    )}
                    {reopenError && (
                      <span className="text-sm text-red-600 text-left">
                        {reopenError}
                      </span>
                    )}
                  </li>
                )}
                {currentStepActions.map((action) => (
                  <li key={action.id} className="flex flex-col gap-2 text-gray-550">
                    <div className="flex items-start justify-start gap-1">
                      {actionButton.title === 'Upload Documents'
                        ? '•'
                        : getCircleStyle(action.completed)}
                      <span className="text-sm">{action.description}</span>
                    </div>
                    {hasGenericEvidence &&
                      Boolean(action.evidenceKind) &&
                      Boolean(aiSystemId) &&
                      canManageRequirementList &&
                      acceptsNewEvidence && (
                        <RequirementEvidenceControl
                          action={action}
                          onSubmitted={handleEvidenceSubmitted}
                          onRequestUpload={handleRequestEvidenceUpload}
                          uploadLabel={isRenewable ? 'Renew' : undefined}
                          showUploadButton={!showFooterEvidenceUpload}
                        />
                      )}
                  </li>
                ))}
              </ul>
            )
          ) : (
            <div className="text-sm text-gray-500">No actions available</div>
          )
        }
        actionButton={
          isRecordStep ? (
            <div className="flex flex-col items-end gap-2">
              {recordSubmitError && (
                <span className="text-xs text-red-600">{recordSubmitError}</span>
              )}
              <PrimaryButton
                onClick={handleGenerateAndSign}
                disabled={
                  locked ||
                  !canManageRequirementList ||
                  submittingRecord ||
                  !record
                }
                size="sm"
              >
                {submittingRecord ? 'Submitting...' : 'Generate & Sign'}
              </PrimaryButton>
            </div>
          ) : (
            showFooterEvidenceUpload ? (
              <div className="flex justify-end">
                <PrimaryButton
                  icon={UploadIcon('#111827', 16)}
                  onClick={() => handleRequestEvidenceUpload(footerEvidenceAction!)}
                  disabled={!canManageRequirementList}
                  size="sm"
                >
                  {evidenceUploadLabel(
                    footerEvidenceAction!.submittedEvidence,
                    isRenewable ? 'Renew' : undefined
                  )}
                </PrimaryButton>
              </div>
            ) : (
              !actionsLoading &&
              !isCheckboxStep &&
              !isUploadStep &&
              !hasGenericEvidence && (
                <div
                  className={`flex ${showCompleteAction ? 'justify-between' : 'justify-end'}`}
                >
                  {showCompleteAction && (
                    <PrimaryButton
                      onClick={actionButton.actionComplete}
                      disabled={!canManageRequirementList}
                      size="sm"
                    >
                      Complete
                    </PrimaryButton>
                  )}
                  <PrimaryButton
                    icon={actionButton.icon}
                    onClick={actionButton.action}
                    disabled={!canManageRequirementList}
                    size="sm"
                  >
                    {actionButton.title}
                  </PrimaryButton>
                </div>
              )
            )
          )
        }
      />

      {/* Sibling of the SideDrawer, not a child: see RequirementEvidenceDocumentModal. */}
      <RequirementEvidenceDocumentModal
        target={evidenceUploadTarget}
        aiSystemId={aiSystemId ?? ''}
        onClose={closeEvidenceUpload}
        onSubmitted={handleEvidenceUploadSubmitted}
        onAlreadySatisfied={handleEvidenceAlreadySatisfied}
      />

      <Modal
        open={showSupportingDocModal}
        setOpen={setShowSupportingDocModal}
        dialogPanelClassnames="w-[820px] overflow-visible"
      >
        <div className="flex flex-col gap-6">
          <p className="text-gray-900 text-lg leading-6 font-medium">
            {isFriaUploadFlow
              ? 'FRIA Document'
              : isDpiaUploadFlow
                ? 'DPIA Document'
                : 'Upload Documents'}
          </p>
          <FileUpload
            suportedFiles={supportedFiles}
            maxFiles={uploadLimits.max}
            minFiles={uploadLimits.min}
            uploading={uploading}
            handleCancel={() => {
              // Reopen the importer drawer we closed when launching this modal.
              const wasImporterUpload = activeUploadActionKey !== null;
              setShowSupportingDocModal(false);
              setSelectedOption('');
              setIsFriaUploadFlow(false);
              setIsDpiaUploadFlow(false);
              setActiveUploadActionKey(null);
              dispatch(resetFileState());
              if (wasImporterUpload) {
                setIsDrawerOpen(true);
              }
            }}
            handleUpload={
              activeUploadActionKey
                ? () =>
                    uploadFiles(
                      files,
                      IMPORTER_UPLOAD_CATEGORY_BY_ACTION_KEY[
                        activeUploadActionKey
                      ]
                    )
                : isFriaUploadFlow
                  ? () => uploadFiles(files, 'FRIA')
                  : isDpiaUploadFlow
                    ? () => uploadFiles(files, 'DPIA')
                    : () =>
                        uploadFiles(
                          files,
                          getUploadCategory(),
                          getConformityAssessmentType()
                        )
            }
          />
        </div>
      </Modal>

      <Modal
        open={showConformityAssessmentModal}
        setOpen={setShowConformityAssessmentModal}
        dialogPanelClassnames="w-[820px]"
      >
        <div className="relative flex flex-col gap-6">
          <p className="text-gray-900 text-lg leading-6 font-medium">
            Conformity Assessment
          </p>
          <div className="flex gap-6">
            {radioButtonCardOptions.map((option) => (
              <RadioButtonCard
                key={option.label}
                label={option.label}
                desctiption={option.description}
                documentRequirement={option.documentRequirement}
                selected={selectedOption === option.label}
                onChange={() => {
                  setSelectedOption(option.label);
                  setConformityRouteError(false);
                }}
              />
            ))}
          </div>
          {conformityRouteError && (
            <p className="absolute bottom-12 text-sm text-red-600">
              Delete all Internal Assessment documents in Documentation before
              switching to External Assessment.
            </p>
          )}
          <div className="flex justify-end gap-3 mt-4">
            <WhiteButton
              onClick={() => setShowConformityAssessmentModal(false)}
            >
              Cancel
            </WhiteButton>
            <PrimaryButton
              onClick={handleConformityNextClick}
              disabled={!selectedOption || conformityRouteError}
              className="w-[82px]"
            >
              Next
            </PrimaryButton>
          </div>
        </div>
      </Modal>

      <Modal
        open={showFRIAOptionsModal}
        setOpen={setShowFRIAOptionsModal}
        dialogPanelClassnames="w-[820px]"
      >
        <div className="flex flex-col gap-6">
          <p className="text-gray-900 text-lg leading-6 font-medium">
            FRIA Document
          </p>
          <div className="flex gap-6">
            {friaOptionsCardOptions.map((option) => (
              <RadioButtonCard
                key={option.label}
                label={option.label}
                desctiption={option.description}
                documentRequirement={option.documentRequirement}
                selected={selectedFRIAOption === option.label}
                onChange={() => setSelectedFRIAOption(option.label)}
              />
            ))}
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <WhiteButton
              onClick={() => {
                setShowFRIAOptionsModal(false);
                setSelectedFRIAOption('');
              }}
            >
              Cancel
            </WhiteButton>
            <PrimaryButton
              onClick={handleFriaOptionsNextClick}
              disabled={!selectedFRIAOption}
              className="w-[82px]"
            >
              Next
            </PrimaryButton>
          </div>
        </div>
      </Modal>

      <Modal
        open={showDPIAOptionsModal}
        setOpen={setShowDPIAOptionsModal}
        dialogPanelClassnames="w-[820px]"
      >
        <div className="flex flex-col gap-6">
          <p className="text-gray-900 text-lg leading-6 font-medium">
            DPIA Document
          </p>
          <div className="flex gap-6">
            {dpiaOptionsCardOptions.map((option) => (
              <RadioButtonCard
                key={option.label}
                label={option.label}
                desctiption={option.description}
                documentRequirement={option.documentRequirement}
                selected={selectedDPIAOption === option.label}
                onChange={() => setSelectedDPIAOption(option.label)}
              />
            ))}
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <WhiteButton
              onClick={() => {
                setShowDPIAOptionsModal(false);
                setSelectedDPIAOption('');
              }}
            >
              Cancel
            </WhiteButton>
            <PrimaryButton
              onClick={handleDpiaOptionsNextClick}
              disabled={!selectedDPIAOption}
              className="w-[82px]"
            >
              Next
            </PrimaryButton>
          </div>
        </div>
      </Modal>

      <Modal
        open={showCompleteModal}
        setOpen={setShowCompleteModal}
        dialogPanelClassnames="w-[475px]"
      >
        <>
          <div className="flex flex-col gap-2">
            <p className="text-gray-900 text-lg leading-6 font-medium">
              Do you want to complete?
            </p>

            <p className="text-sm text-gray-500">
              Once you click Complete, this step will be permanently locked.
              <br />
              No further uploads or changes will be allowed.
            </p>

            <p className="text-sm text-gray-500">
              The workflow will be marked as Completed, and the AI system status
              will update to Compliant.
            </p>
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <WhiteButton
              onClick={() => {
                setShowCompleteModal(false);
              }}
            >
              Cancel
            </WhiteButton>
            <PrimaryButton
              onClick={handleCompleteButton}
              disabled={finishingCompliance}
            >
              {finishingCompliance ? 'Completing...' : 'Complete'}
            </PrimaryButton>
          </div>
        </>
      </Modal>

      {showToast && (
        <Toast
          setShowToast={setShowToast}
          showToast={showToast}
          text={
            uploadedCount > 1 ? 'Documents Uploaded!' : 'Document Uploaded!'
          }
        />
      )}
    </div>
  );
}
